import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { BuildEaseLogo } from "@/components/BuildEaseLogo";
import { HUDStatusBadge } from "@/components/HUDStatusBadge";
import { InstallPrompt } from "@/components/InstallPrompt";

export const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", to: "/dashboard" },
  { id: "scan", label: "Scan", to: "/scan" },
  { id: "materials", label: "Materials", to: "/materials" },
  { id: "green", label: "Green", to: "/green" },
] as const;

export const REPORT_ITEMS = [
  { id: "costs", label: "Cost Analytics", short: "Costs", to: "/costs", blurb: "Where the money goes and how sure we are" },
  { id: "compliance", label: "Compliance Checklist", short: "Compliance", to: "/compliance", blurb: "What's approved, pending or needs a look" },
] as const;

const sideLink = (isActive: boolean) =>
  cn(
    "flex items-center justify-between rounded-lg px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-200",
    isActive ? "bg-safety/10 text-safety" : "text-white/60 hover:bg-white/5 hover:text-white",
  );

const tabLink = (isActive: boolean) =>
  cn(
    "flex flex-1 flex-col items-center gap-2 py-3.5 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors duration-200",
    isActive ? "text-safety" : "text-white/50",
  );

const Dot = () => <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-safety" />;
const Bar = ({ on }: { on: boolean }) => <span aria-hidden className={cn("h-0.5 w-6 rounded-full", on ? "bg-safety" : "bg-transparent")} />;

// Responsive navigation shell: text-only bottom-tab HUD below 768px, sidebar at/above.
export function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const reportsActive = REPORT_ITEMS.some((r) => pathname.startsWith(r.to));

  useEffect(() => setSheetOpen(false), [pathname]);

  return (
    <div data-testid="app-shell" className="min-h-screen bg-obsidian text-white">
      <aside data-testid="app-sidebar" className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-cad/25 bg-obsidian-deep/80 backdrop-blur-xl md:flex">
        <div className="border-b border-cad/20 px-5 py-6">
          <BuildEaseLogo height={34} />
        </div>
        <nav className="flex flex-col gap-1 px-3 py-6">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.id} to={item.to} data-testid={`nav-${item.id}`} className={({ isActive }) => sideLink(isActive)}>
              {({ isActive }) => (<>{item.label}{isActive && <Dot />}</>)}
            </NavLink>
          ))}
          <span className="mt-5 px-4 pb-2 font-mono text-[9px] uppercase tracking-[0.26em] text-cad/70">Reports</span>
          {REPORT_ITEMS.map((item) => (
            <NavLink key={item.id} to={item.to} data-testid={`nav-${item.id}`} className={({ isActive }) => sideLink(isActive)}>
              {({ isActive }) => (<>{item.label}{isActive && <Dot />}</>)}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-3 border-t border-cad/20 px-3 py-5">
          <NavLink to="/billing" data-testid="nav-billing" className={({ isActive }) => sideLink(isActive)}>
            {({ isActive }) => (<>Plans &amp; Billing{isActive && <Dot />}</>)}
          </NavLink>
          <NavLink to="/settings" data-testid="nav-settings" className={({ isActive }) => sideLink(isActive)}>
            {({ isActive }) => (<>Settings{isActive && <Dot />}</>)}
          </NavLink>
          <div className="px-1 pt-1">
            <HUDStatusBadge status="Online" tone="online" />
          </div>
        </div>
      </aside>

      <nav
        data-testid="app-bottom-nav"
        className="fixed inset-x-0 bottom-0 z-40 flex items-stretch border-t border-cad/25 bg-obsidian-deep/90 backdrop-blur-xl md:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {NAV_ITEMS.map((item) => (
          <NavLink key={item.id} to={item.to} data-testid={`nav-tab-${item.id}`} className={({ isActive }) => tabLink(isActive)}>
            {({ isActive }) => (<>{item.label}<Bar on={isActive} /></>)}
          </NavLink>
        ))}
        <button type="button" data-testid="nav-tab-reports" aria-expanded={sheetOpen} onClick={() => setSheetOpen(true)} className={tabLink(reportsActive)}>
          Reports
          <Bar on={reportsActive} />
        </button>
      </nav>

      <AnimatePresence>
        {sheetOpen && (
          <>
            <motion.button
              type="button"
              aria-label="Close reports menu"
              data-testid="reports-sheet-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSheetOpen(false)}
              className="fixed inset-0 z-40 bg-obsidian/70 backdrop-blur-sm md:hidden"
            />
            <motion.div
              role="dialog"
              aria-label="Reports"
              data-testid="reports-sheet"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 380, damping: 36 }}
              className="fixed inset-x-0 bottom-0 z-50 rounded-t-3xl border-t border-cad/40 bg-obsidian-deep p-5 pb-8 md:hidden"
              style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom))" }}
            >
              <span aria-hidden className="mx-auto mb-5 block h-1 w-10 rounded-full bg-cad/60" />
              <span className="px-1 font-mono text-[10px] uppercase tracking-[0.24em] text-cad">Reports</span>
              <div className="mt-3 flex flex-col gap-2">
                {REPORT_ITEMS.map((item) => {
                  const active = pathname.startsWith(item.to);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      data-testid={`reports-sheet-${item.id}`}
                      onClick={() => navigate(item.to)}
                      className={cn(
                        "flex items-center justify-between rounded-2xl border px-4 py-4 text-left transition-colors duration-200",
                        active ? "border-safety/60 bg-safety/10" : "border-cad/30 bg-white/[0.02] hover:border-cad/60",
                      )}
                    >
                      <span className="flex flex-col gap-1">
                        <span className={cn("font-display text-base font-semibold", active ? "text-safety" : "text-white")}>{item.label}</span>
                        <span className="font-sans text-xs text-white/50">{item.blurb}</span>
                      </span>
                      {active && <Dot />}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                data-testid="reports-sheet-billing"
                onClick={() => navigate("/billing")}
                className={cn(
                  "mt-2 flex items-center justify-between rounded-2xl border px-4 py-4 text-left transition-colors duration-200",
                  pathname.startsWith("/billing") ? "border-safety/60 bg-safety/10" : "border-cad/30 bg-white/[0.02] hover:border-cad/60",
                )}
              >
                <span className={cn("font-display text-base font-semibold", pathname.startsWith("/billing") ? "text-safety" : "text-white")}>Plans &amp; Billing</span>
                {pathname.startsWith("/billing") && <Dot />}
              </button>
              <button
                type="button"
                data-testid="reports-sheet-settings"
                onClick={() => navigate("/settings")}
                className={cn(
                  "mt-2 flex items-center justify-between rounded-2xl border px-4 py-4 text-left transition-colors duration-200",
                  pathname.startsWith("/settings") ? "border-safety/60 bg-safety/10" : "border-cad/30 bg-white/[0.02] hover:border-cad/60",
                )}
              >
                <span className={cn("font-display text-base font-semibold", pathname.startsWith("/settings") ? "text-safety" : "text-white")}>Settings</span>
                {pathname.startsWith("/settings") && <Dot />}
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <main className="pb-24 md:pb-0 md:pl-60">{children}</main>
      <InstallPrompt />
    </div>
  );
}
